import React, {useState} from 'react';
import {MembershipPlan, MembershipPlanData} from "types/MembershipPlan";
import {Form, Formik, FormikHelpers, FormikProps} from "formik";
import Label from "components/Form/Label";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import FieldHeader from "components/Form/FieldHeader";
import {Tab} from "@headlessui/react";
import TabButton from "components/TabButton";
import {ExtendedDurationPicker} from "components/Form/DurationPicker";
import MoneyInput from "components/Form/MoneyInput";
import {OrganisationPreview} from "types/Organisation";
import Select, {Option} from "components/Form/Select/Select";
import IconName from "types/IconName";
import ngage from "services/ngage";
import {DurationUnit} from "../../../components/Form/DurationPicker/ExtendedDurationPicker";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {TextAreaField} from "../../../components/Form/Form";
import {useOperator} from "../../../context/OperatorContext";

enum PriceMode {FREE, CHARGE}
enum OrganisationMode {ALL, SELECTED}

type Props = {
    membershipPlan?: MembershipPlan
    onSubmit: (values: MembershipPlanData, helpers: FormikHelpers<MembershipPlanData>) => any
}

const organisationOption = (organisation: OrganisationPreview): Option => ({
    id: organisation.id,
    name: organisation.name,
    icon: IconName.GLOBE_ALT
})

function MembershipPlanForm(props: Props) {
    const {termFor} = useOperator();
    const [priceMode, setPriceMode] = useState<PriceMode>(props.membershipPlan?.price? PriceMode.CHARGE : PriceMode.FREE);

    const [organisationMode, setOrganisationMode] = useState<OrganisationMode>((!!props.membershipPlan?.organisations?.length)? OrganisationMode.SELECTED : OrganisationMode.ALL);
    const [selectedOrganisations, setSelectedOrganisations] = useState<Array<Option>>(props.membershipPlan?.organisations?.map(organisationOption) || []);

    const selectPriceMode = (mode: PriceMode, {setFieldValue}: FormikHelpers<MembershipPlanData>) => {
        void setFieldValue('price', mode === PriceMode.FREE? 0 : (props.membershipPlan?.price || 1000))
        setPriceMode(mode)
    }

    function selectOrganisationMode(mode: OrganisationMode, {setFieldValue}: FormikHelpers<MembershipPlanData>) {
        void setFieldValue('organisation_ids', [])
        setSelectedOrganisations([])
        setOrganisationMode(mode)
    }

    const organisationSource = async (query: string) => {
        return (await ngage.organisations.filtered({search: query || null}))?.data.map(organisationOption) || []
    }

    const handleSelectOrganisations = (organisations: Array<Option>, formik: FormikProps<MembershipPlanData>) => {
        void formik.setFieldValue('organisation_ids', organisations.map(organisation => organisation.id))
        setSelectedOrganisations(organisations)
    }

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("This field is required").max(255, "Maximum of 255 characters"),
        description: Yup.string().required("This field is required").max(255, "Maximum of 255 characters"),
        duration: Yup.string().notOneOf(["PT0S"], "This field is required").required("This field is required"),
        maximum_members: Yup.number()
          .min(1, "Must be at least 1")
          .max(100, "Cannot be over 100")
          .integer('Must be a whole number (no decimals allowed)')
          .required("This field is required"),
    })

    const initialValues: MembershipPlanData = {
        name: props.membershipPlan?.name || '',
        description: props.membershipPlan?.description || '',
        duration: props.membershipPlan?.duration.toISO() || 'P1Y',
        price: props.membershipPlan?.price || 0,
        maximum_members: props.membershipPlan?.maximum_members || 1,
        organisation_ids: props.membershipPlan?.organisations?.map(org => org.id) || []
    }

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
            {formik => {
                return (
                    <Form className="space-y-8 divide-y divide-gray-200">
                        <ErrorNotification formik={formik} />
    
                        <div className="shadow sm:rounded-md">
                            <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                                <div className="space-y-8 divide-y divide-gray-200">
                                    <div>
                                        <div>
                                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                                Key Information
                                            </h3>
                                            <p className="mt-1 text-sm text-gray-500">
                                                Basic details about the {termFor('membership plan')}
                                            </p>
                                        </div>
                                        <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
    
                                            {/* Name */}
                                            <div className="sm:col-span-3">
                                                <Label htmlFor="name">Name *</Label>
                                                <div className="mt-1">
                                                    <Field
                                                        type="text"
                                                        name="name"
                                                        id="name"
                                                        autoComplete="off"
                                                        // maxLength="255"
                                                        autoFocus
                                                    />
                                                </div>
                                            </div>
    
                                            {/* Description */}
                                            <div className="sm:col-span-5">
                                                <div className="mt-1">
                                                    <TextAreaField
                                                        name="description"
                                                        label='Description *'
                                                        hint='255 chars max'
                                                        id="description"
                                                        autoComplete="off"
                                                        maxChars={255}
                                                        rows={3}
                                                    />
                                                </div>
                                            </div>
    
                                            {/* Duration */}
                                            <div className="sm:col-span-6 sm:col-start-1">
                                                <Label htmlFor="duration">Duration *</Label>
                                                <div className="mt-1">
                                                    <ExtendedDurationPicker
                                                        units={[DurationUnit.YEAR, DurationUnit.MONTH, DurationUnit.WEEK, DurationUnit.DAY]}
                                                        value={initialValues.duration}
                                                        onChange={duration => formik.setFieldValue('duration', duration.toISO())}
                                                    />

                                                    {(!!formik.errors?.duration && !!formik.touched?.duration) &&
                                                      <FieldHelp isError>
                                                          {formik.errors.duration}
                                                      </FieldHelp>
                                                    }
                                                </div>
                                            </div>
    
                                            {/* Price */}
                                            <div className="sm:col-span-3 sm:col-start-1 md:col-span-2 md:col-start-1 xl:col-span-1 xl:col-start-1">
                                                <FieldHeader label="Price *" />
                                                <Tab.Group selectedIndex={priceMode} onChange={index => selectPriceMode(index, formik)}>
                                                    <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                                                        <TabButton position={TabButton.positions.FIRST}>Free</TabButton>
                                                        <TabButton position={TabButton.positions.LAST}>Charge</TabButton>
                                                    </Tab.List>
                                                    <Tab.Panels>
                                                        <Tab.Panel/>
                                                        <Tab.Panel>
                                                            <MoneyInput
                                                                currency={MoneyInput.Currency.GBP}
                                                                value={formik.values.price}
                                                                onChange={amount => formik.setFieldValue('price', amount)}
                                                            />
                                                        </Tab.Panel>
                                                    </Tab.Panels>
                                                </Tab.Group>
                                            </div>
    
                                            {/* Max Members */}
                                            <div className="sm:col-span-4 sm:col-start-1 md:col-span-3 md:col-start-1 xl:col-span-1 xl:col-start-1">
                                                <Label htmlFor="maximum_members">Maximum Members *</Label>
                                                <div className="mt-1">
                                                    <Field
                                                        type="number"
                                                        name="maximum_members"
                                                        id="maximum_members"
                                                        autoComplete="off"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
    
                                    <div className="pt-8">
                                        <div>
                                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                                Organisations
                                            </h3>
                                            <p className="mt-1 text-sm text-gray-500">
                                                Organisations to which this {termFor('membership plan')} relates
                                            </p>
                                        </div>
                                        <div className="mt-6">
    
                                            <Tab.Group selectedIndex={organisationMode} onChange={index => selectOrganisationMode(index, formik)}>
                                                <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                                                    <TabButton position={TabButton.positions.FIRST}>All</TabButton>
                                                    <TabButton position={TabButton.positions.LAST}>Selected</TabButton>
                                                </Tab.List>
                                                <Tab.Panels>
                                                    <Tab.Panel/>
                                                    <Tab.Panel className="mt-2 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                                        {/* Organisations */}
                                                        <div className="sm:col-span-6">
                                                            <Select
                                                                label="Organisations"
                                                                hint="Type to search"
                                                                source={organisationSource}
                                                                selected={selectedOrganisations}
                                                                onChange={selected => handleSelectOrganisations(selected, formik)}
                                                                multiple
                                                            />
                                                        </div>
                                                    </Tab.Panel>
                                                </Tab.Panels>
                                            </Tab.Group>
                                        </div>
                                    </div>
                                </div>
                            </div>
    
                            <FormControls formik={formik} />
                        </div>
                    </Form>
                )
            }}
        </Formik>
    );
}

export default MembershipPlanForm;